import React from "react";

import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Tooltip } from "@material-ui/core";
import styled from "styled-components/macro";

import Clickable from "components/Button/Clickable";

const ButtonWrapper = styled(Clickable)(
  ({ color, disabled, theme, inline }) => `
  opacity: ${disabled ? "0.66" : "initial"};
  margin: ${theme.space[1]}px ${inline ? 0 : theme.space[2]}px;
  color: ${disabled ? theme.colors.gray78 : theme.colors[color]};
  display: flex;
  align-items: center;
  &:hover {
    ${disabled ? "" : "opacity: 0.66;"}
  }
`,
);

const ButtonIcon = styled(FontAwesomeIcon)(
  ({ iconPosition }) => `
  margin-left: ${iconPosition === "right" ? "6px" : 0};
  margin-right: ${iconPosition === "right" ? 0 : "6px"};
`,
);

interface IconTextButtonProps {
  children?: React.ReactNode;
  color?: string;
  icon?: IconProp;
  iconPosition?: "left" | "right";
  inline?: boolean;
  tooltip?: string;
  style?: React.CSSProperties;
  onClick?: React.MouseEventHandler;
  as?: React.ElementType;
  to?: string;
  iconSize?: number;
  textSize?: number;
  disabled?: boolean;
  dataTour?: string;
  target?: string;
  tabIndex?: number;
}

const IconTextButton = ({
  children,
  icon,
  color = "primary",
  style,
  onClick,
  as,
  to,
  iconSize = 16,
  textSize = 12,
  tooltip,
  disabled,
  dataTour,
  inline = false,
  target,
  tabIndex,
  iconPosition = "left",
}: IconTextButtonProps): React.JSX.Element => {
  const button = (
    <ButtonWrapper
      as={as}
      to={to}
      onClick={onClick}
      color={color}
      style={style}
      target={target}
      type="button"
      disabled={disabled}
      inline={inline ? 1 : 0}
      data-tour={dataTour}
      tabIndex={tabIndex}
    >
      {icon && iconPosition === "left" ? (
        <ButtonIcon
          iconPosition={iconPosition}
          icon={icon}
          style={{ fontSize: iconSize }}
        />
      ) : null}
      <span style={{ fontSize: textSize }}>{children}</span>
      {icon && iconPosition === "right" ? (
        <ButtonIcon
          iconPosition={iconPosition}
          icon={icon}
          style={{ fontSize: iconSize }}
        />
      ) : null}
    </ButtonWrapper>
  );
  return tooltip ? <Tooltip title={tooltip}>{button}</Tooltip> : button;
};

export default IconTextButton;

export const LargeIconTextButton = (
  props: IconTextButtonProps,
): React.JSX.Element => <IconTextButton {...props} iconSize={20} />;

export const TableIconTextButton = (
  props: IconTextButtonProps,
): React.JSX.Element => (
  <IconTextButton
    {...props}
    iconSize={14}
    style={{
      ...props.style,
      textDecoration: "underline",
      textTransform: "uppercase",
    }}
    textSize={14}
  />
);
